// Keeps the persistent settings rail stable across Turbo navigations.
//
// The rail is marked data-turbo-permanent, so Turbo carries it into each new page
// instead of re-rendering it. Two things still need fixing up in JS:
//
//   1. Active highlight — the carried-over rail keeps its old server-set highlight,
//      so we recompute the active link from the URL after every navigation (and
//      optimistically on click, for instant feedback).
//   2. Scroll position — Turbo transplants the element (detach + reattach), which
//      resets the inner scroll to 0 and may re-instantiate this controller. We stash
//      the scroll position on the scroller element itself (an expando property that
//      rides along with the transplanted node) and restore it on connect and after
//      every navigation, so clicking a lower item doesn't jump the rail to the top.
//
// Example usage:
// <aside data-controller="settings-nav" data-action="click->settings-nav#onLinkClick">
//   <div data-settings-nav-target="scroller">…</div>
// </aside>

import { Controller } from "@hotwired/stimulus"

const ACTIVE_CLASSES = ["bg-gray-100", "dark:bg-neutral-700"]
const SCROLL_KEY = "__settingsNavScrollTop"

export default class extends Controller {
  static targets = ["scroller"]

  connect() {
    this.onNavigated = this.onNavigated.bind(this)
    this.rememberScroll = this.rememberScroll.bind(this)

    if (this.hasScrollerTarget) {
      this.scrollerTarget.addEventListener("scroll", this.rememberScroll, { passive: true })
    }
    // turbo:render restores early (before paint) to avoid a flash; turbo:load is a
    // belt-and-suspenders pass. Both — and connect — are idempotent.
    document.addEventListener("turbo:render", this.onNavigated)
    document.addEventListener("turbo:load", this.onNavigated)

    this.restoreScroll()
    this.update()
  }

  disconnect() {
    if (this.hasScrollerTarget) {
      this.scrollerTarget.removeEventListener("scroll", this.rememberScroll)
    }
    document.removeEventListener("turbo:render", this.onNavigated)
    document.removeEventListener("turbo:load", this.onNavigated)
  }

  onNavigated() {
    this.restoreScroll()
    this.update()
  }

  rememberScroll() {
    this.scrollerTarget[SCROLL_KEY] = this.scrollerTarget.scrollTop
  }

  restoreScroll() {
    if (!this.hasScrollerTarget) return
    const saved = this.scrollerTarget[SCROLL_KEY]
    if (saved != null) this.scrollerTarget.scrollTop = saved
  }

  // Optimistically highlight the clicked link before navigation completes.
  onLinkClick(event) {
    const link = event.target.closest("a[href^='/']")
    if (link) this.activate(link)
  }

  // Pick the longest href that prefixes the current path, so a nested page
  // (e.g. /settings/branding/community_site) keeps its parent active and
  // /settings only matches on the index itself.
  update() {
    const path = window.location.pathname
    let best = null
    let bestLength = -1

    this.links().forEach((link) => {
      const href = link.getAttribute("href")
      if ((path === href || path.startsWith(`${href}/`)) && href.length > bestLength) {
        best = link
        bestLength = href.length
      }
    })

    this.activate(best)
  }

  activate(activeLink) {
    this.links().forEach((link) => {
      const active = link === activeLink
      ACTIVE_CLASSES.forEach((cls) => link.classList.toggle(cls, active))
      if (active) {
        link.setAttribute("aria-current", "page")
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  links() {
    return Array.from(this.element.querySelectorAll("a[href^='/']"))
  }
}
